/**
 * DigitaloceanSpacesExportCsvJsonPage — read-only report for `digitalocean-spaces-export-csv-json`.
 * Numbers-only table. GET /api/v1/digitalocean-spaces-export-csv-json returns items[] the table renders.
 */
import { useDigitaloceanSpacesExportCsvJsonList } from "./hooks";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const TH = "px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-zinc-500";
const TD = "px-3 py-2 text-sm text-zinc-900";

export function DigitaloceanSpacesExportCsvJsonPage() {
	const listQuery = useDigitaloceanSpacesExportCsvJsonList();
	const items = listQuery.data?.items ?? [];

	const columns =
		items.length > 0 ? Object.keys(items[0]).filter((k) => k !== "tenant_id") : ["id", "value"];

	return (
		<main
			data-testid="digitalocean-spaces-export-csv-json-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Report
				</p>
				<h1 className="text-green-700 text-3xl font-semibold">
					DigitaloceanSpacesExportCsvJson
				</h1>
			</header>

			<section className={CARD} aria-label="DigitaloceanSpacesExportCsvJson report">
				{listQuery.isLoading ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="digitalocean-spaces-export-csv-json-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p className="text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p className="text-sm text-zinc-500" data-testid="digitalocean-spaces-export-csv-json-empty">
						No data.
					</p>
				) : (
					<div className="overflow-x-auto">
						<table
							className="w-full border-collapse"
							data-testid="digitalocean-spaces-export-csv-json-table"
						>
							<thead className="bg-zinc-50">
								<tr>
									{columns.map((c) => (
										<th key={c} className={TH}>
											{c}
										</th>
									))}
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-200">
								{items.map((row) => (
									<tr key={row.id} data-testid={`digitalocean-spaces-export-csv-json-row-${row.id}`}>
										{columns.map((c) => (
											<td key={c} className={`${TD} tabular-nums`}>
												{typeof row[c] === "number"
													? (row[c] as number).toLocaleString()
													: String(row[c] ?? "—")}
											</td>
										))}
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</main>
	);
}

export default DigitaloceanSpacesExportCsvJsonPage;
